'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { useCart } from '@/components/CartContext';

const navLinks = [
  { href: '/homepage', label: 'Home' },
  { href: '/about', label: 'Our Story' },
  { href: '/products', label: 'Shop' },
];

export default function Header() {
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { itemCount } = useCart();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const isActive = (href: string) => pathname === href || pathname?.startsWith(href);

  return (
    <>
      <header
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-[#FAF8F3]/95 backdrop-blur-md border-b border-[rgba(201,169,110,0.15)] py-4'
            : 'bg-transparent py-6'
        }`}
      >
        <div className="max-w-8xl mx-auto px-6 lg:px-10 flex items-center justify-between">
          {/* Logo */}
          <Link href="/homepage">
            <AppLogo
              text="LuxeBeauty"
              iconName="SparklesIcon"
              size={64}
              className="cursor-pointer"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-10">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className={`nav-link ${isActive(link.href) ? 'active' : ''}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-5">
            {/* Search */}
            <button
              className="hidden md:flex items-center gap-2 text-[11px] font-semibold tracking-wider uppercase text-[#8A7E72] hover:text-[#C9A96E] transition-colors"
              aria-label="Search"
            >
              <Icon name="MagnifyingGlassIcon" size={18} className="text-[#8A7E72]" />
            </button>

            {/* Cart */}
            <Link href="/cart" className="relative">
              <button
                className="flex items-center gap-2 text-[#1A1612] hover:text-[#C9A96E] transition-colors"
                aria-label={`Cart with ${itemCount} items`}
              >
                <Icon name="ShoppingBagIcon" size={20} />
                {itemCount > 0 && <span className="cart-badge">{itemCount}</span>}
              </button>
            </Link>

            {/* Shop CTA */}
            <Link href="/products" className="hidden md:block">
              <button className="btn-gold text-[10px] py-2.5 px-5">Shop Now</button>
            </Link>

            {/* Mobile Hamburger */}
            <button
              className="lg:hidden flex flex-col gap-1.5 p-1"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
            >
              <span className="w-5 h-px bg-[#1A1612] block" />
              <span className="w-5 h-px bg-[#1A1612] block" />
              <span className="w-3 h-px bg-[#1A1612] block" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <div className={`mobile-menu-overlay ${mobileOpen ? 'visible' : 'hidden'}`}>
        <button
          onClick={() => setMobileOpen(false)}
          className="absolute top-6 right-6 text-[#FAF8F3] hover:text-[#C9A96E] transition-colors"
          aria-label="Close menu"
        >
          <Icon name="XMarkIcon" size={28} className="text-[#FAF8F3]" />
        </button>

        <div className="text-center space-y-2">
          <p className="label-gold mb-8 block" style={{ color: '#C9A96E' }}>
            Navigation
          </p>
          {navLinks.map((link) => (
            <Link key={link.href} href={link.href} onClick={() => setMobileOpen(false)}>
              <div
                className={`font-fraunces text-4xl font-light py-3 transition-colors ${
                  isActive(link.href) ? 'text-[#C9A96E]' : 'text-[#FAF8F3] hover:text-[#C9A96E]'
                }`}
              >
                {link.label}
              </div>
            </Link>
          ))}
          <div className="pt-6">
            <Link href="/cart" onClick={() => setMobileOpen(false)}>
              <div className="text-[#FAF8F3] hover:text-[#C9A96E] transition-colors font-fraunces text-4xl font-light py-3">
                Cart {itemCount > 0 && `(${itemCount})`}
              </div>
            </Link>
          </div>
        </div>

        <Link href="/products" onClick={() => setMobileOpen(false)}>
          <button className="btn-gold mt-4">Explore Collection</button>
        </Link>
      </div>
    </>
  );
}
